import { useMemo, useRef, useState, type ReactNode } from 'react'
import { configureHttpClient } from '@/lib/http'
import { MOCK_PERSONAS, mockTokenFor, resolveMockRole, type MockRole } from './mockPersonas'
import { AuthSessionContext, type AuthSession } from './session'

interface MockSessionProviderProps {
  children: ReactNode
  /** Força a persona (testes). */
  role?: MockRole
}

/** Sessão falsa para dev/testes. Só é montada quando VITE_USE_MOCK_API=true (ver config/env). */
export function MockSessionProvider({ children, role }: MockSessionProviderProps) {
  const [activeRole] = useState<MockRole>(() => role ?? resolveMockRole())
  const [signedIn, setSignedIn] = useState(true)

  const session = useMemo<AuthSession>(() => {
    const persona = MOCK_PERSONAS[activeRole]
    return {
      mode: 'mock',
      status: signedIn ? 'authenticated' : 'unauthenticated',
      error: null,
      profile: signedIn ? persona : null,
      getAccessToken: () => (signedIn ? mockTokenFor(activeRole) : null),
      returnTo: () => '/',
      signIn: async () => setSignedIn(true),
      signOut: async () => setSignedIn(false),
      renewToken: async () => undefined,
    }
  }, [activeRole, signedIn])

  const sessionRef = useRef(session)
  sessionRef.current = session
  // Configurado já no primeiro render: efeitos dos filhos (queries) rodam antes dos do pai.
  useState(() =>
    configureHttpClient({
      getAccessToken: () => sessionRef.current.getAccessToken(),
      onUnauthorized: () => setSignedIn(false),
    }),
  )

  return <AuthSessionContext.Provider value={session}>{children}</AuthSessionContext.Provider>
}
